/** Root freeze October13; docs/provider-verification-contract.md root decisions govern. */
import type {Actor,AuditRecord,ListResult} from './contracts';

/** Stage-1 provider dictionary; root extends only via a frozen contract change. */
export const PROVIDER_KEYS=['openlane-canada'] as const;
export type ProviderKey=typeof PROVIDER_KEYS[number];

/** Enrollment aspects state the paid business relationship; permission aspects state
 *  account capabilities a staff member actually observed. Neither is an integration claim. */
export type ProviderAspectKind='enrollment'|'permission';
export type ProviderAspect='membership-active'|'membership-tier'|'fee-schedule'
 |'account-export'|'api-entitlement'|'data-feed'|'content-reuse';

export const PROVIDER_ASPECTS:{aspect:ProviderAspect;kind:ProviderAspectKind;label:string}[]=[
 {aspect:'membership-active',kind:'enrollment',label:'Paid membership held'},
 {aspect:'membership-tier',kind:'enrollment',label:'Membership tier/plan as shown in account'},
 {aspect:'fee-schedule',kind:'permission',label:'Fee schedule seen in account'},
 {aspect:'account-export',kind:'permission',label:'Account exports available'},
 {aspect:'api-entitlement',kind:'permission',label:'Account source states API access'},
 {aspect:'data-feed',kind:'permission',label:'Data feed available'},
 {aspect:'content-reuse',kind:'permission',label:'Content reuse statement observed'}];

/** What the staff member concluded from the source. 'verified' = observed to hold;
 *  'unavailable' = explicitly observed NOT to hold; not found is unclear; 'unclear' = checked,
 *  could not determine. Absence of any record is the separate state 'unknown'. */
export type ProviderFinding='verified'|'unavailable'|'unclear';

/** Typed per-aspect payload; text labels are staff-entered as displayed by the provider,
 *  trimmed and bounded; none is a credential, amount or entitlement grant. */
export type ProviderAspectValue=
 |{aspect:'membership-active';held:boolean|null}
 |{aspect:'membership-tier';tierLabel:string|null}
 |{aspect:'fee-schedule';seen:boolean|null}
 |{aspect:'account-export';available:boolean|null;exportLabel:string|null}
 |{aspect:'api-entitlement';entitlementShown:boolean|null}
 |{aspect:'data-feed';available:boolean|null;feedLabel:string|null}
 |{aspect:'content-reuse';statementText:string|null};

export type ProviderVerificationSourceKind='account-page'|'invoice-statement'
 |'provider-correspondence'|'support-ticket'|'published-terms'|'other-manual';

export type ProviderVerificationInput={
 providerKey:ProviderKey;
 value:ProviderAspectValue;          // the aspect + its typed payload drive every gate
 finding:ProviderFinding;
 observedOn:string;                  // real calendar YYYY-MM-DD the observation was made
 sourceKind:ProviderVerificationSourceKind;
 sourceCitation:string;              // trimmed 1..500; locates the fact in the source; never a credential
 scope:string;                       // trimmed 0..200, e.g. 'OPENLANE Canada marketplace account'
 evidenceAssetId?:string|null;       // optional retained private unlinked business original
 notes:string};                      // trimmed 0..4000

export type ProviderVerificationRecord=ProviderVerificationInput&{
 id:string;number:string;sequence:number;   // PV-000001… in-transaction monotonic
 aspect:ProviderAspect;                     // mirrored from value.aspect for SQL filtering
 status:'active'|'void';version:number;
 actorId:string;createdAt:string;updatedAt:string;
 voidReason:string;voidedAt:string|null;voidedByActorId:string|null};

export type ProviderVerificationRevision={id:string;verificationId:string;version:number;
 action:'recorded'|'replaced'|'voided';reason:string;
 record:ProviderVerificationRecord;   // full record as stored; ascending replay 1..n reproduces the live row
 payloadHash:string;                  // SHA-256 of exactly the stored revision record_json bytes
 actorId:string;createdAt:string};

/** Per-aspect live state. 'unknown' exactly when no active record exists. */
export type ProviderAspectState={
 providerKey:ProviderKey;aspect:ProviderAspect;kind:ProviderAspectKind;
 state:'unknown'|'verified'|'unavailable'|'unclear';
 value:ProviderAspectValue|null;      // null exactly when unknown
 observedOn:string|null;
 fromId:string|null;fromNumber:string|null;version:number|null;
 reviewedByActorId:string|null;reviewedAt:string|null;
 evidenceAssetId:string|null;
 historyCount:number};                // all records ever for this aspect, any status

export type ProviderVerificationSummary={generatedAt:string;
 providers:{providerKey:ProviderKey;
  aspects:ProviderAspectState[];      // ALL seven aspects always present, unknown included
  verifiedCount:number;unavailableCount:number;unclearCount:number;unknownCount:number}[]};

export type ProviderVerificationDetail={record:ProviderVerificationRecord;
 revisions:ProviderVerificationRevision[];   // newest first, capped at 200
 revisionTotal:number;revisionsTruncated:boolean;  // full history is never deleted
 activity:AuditRecord[]};                    // newest first, capped at 200

export type ProviderVerificationQuery={search?:string;providerKey?:ProviderKey;
 aspect?:ProviderAspect;status?:'active'|'void';page?:number;pageSize?:number};

export interface ProviderVerificationGateway{
 getSummary(actor:Actor):ProviderVerificationSummary;
 listVerifications(query:ProviderVerificationQuery,actor:Actor):ListResult<ProviderVerificationRecord>;
 getVerification(id:string,actor:Actor):ProviderVerificationDetail;
 recordVerification(input:ProviderVerificationInput,actor:Actor,key:string):ProviderVerificationDetail;            // app-channel only
 replaceVerification(id:string,input:ProviderVerificationInput,reason:string,
  expectedVersion:number,actor:Actor,key:string):ProviderVerificationDetail;                                       // app-channel only
 voidVerification(id:string,reason:string,expectedVersion:number,actor:Actor,key:string):ProviderVerificationDetail;} // app-channel only
